import { ProjectItem } from '../types/project';
import { Reviewer, UserSearchResult } from '../types/review';

const CO_OWNER_SEPARATOR = ',';
const EMAIL_SEPARATOR = '@';

export const PROJECT_STEWARD_PARTICIPANT_MESSAGE =
  'Project owners and co-owners cannot be review participants.';

type ProjectOwnershipContext = Pick<ProjectItem, 'owner' | 'co_owners'> | null;
type AuthLikeUser = {
  email?: string | null;
  username?: string | null;
} | null | undefined;

const normalizeIdentifier = (value: string | null | undefined): string | null => {
  const normalizedValue = value?.trim().toLowerCase() ?? null;
  return normalizedValue && normalizedValue.length > 0
    ? normalizedValue
    : null;
};

const getEmailLocalPart = (value: string | null | undefined): string | null => {
  const normalizedValue = normalizeIdentifier(value);
  if (!normalizedValue?.includes(EMAIL_SEPARATOR)) {
    return normalizedValue;
  }

  return normalizedValue.split(EMAIL_SEPARATOR)[0] ?? null;
};

const addIdentifier = (identifiers: Set<string>, value: string | null | undefined) => {
  const normalizedValue = normalizeIdentifier(value);
  if (normalizedValue) {
    identifiers.add(normalizedValue);
  }

  const localPart = getEmailLocalPart(value);
  if (localPart) {
    identifiers.add(localPart);
  }
};

const hasIdentifierOverlap = (
  leftIdentifiers: Set<string>,
  rightIdentifiers: Set<string>,
): boolean => Array.from(leftIdentifiers).some((identifier) => rightIdentifiers.has(identifier));

const getUserIdentifiers = (user: AuthLikeUser): Set<string> => {
  const identifiers = new Set<string>();
  addIdentifier(identifiers, user?.email);
  addIdentifier(identifiers, user?.username);
  return identifiers;
};

export const getProjectStewardIdentifiers = (
  project: ProjectOwnershipContext,
): Set<string> => {
  const identifiers = new Set<string>();
  if (!project) {
    return identifiers;
  }

  addIdentifier(identifiers, project.owner);
  const coOwners = project.co_owners?.split(CO_OWNER_SEPARATOR) ?? [];
  coOwners.forEach((coOwner) => addIdentifier(identifiers, coOwner));

  return identifiers;
};

export const isCurrentUserProjectSteward = (
  user: AuthLikeUser,
  project: ProjectOwnershipContext,
): boolean => hasIdentifierOverlap(
  getUserIdentifiers(user),
  getProjectStewardIdentifiers(project),
);

export const isProjectStewardUser = (
  user: Pick<UserSearchResult, 'email' | 'username'>,
  project: ProjectOwnershipContext,
): boolean => hasIdentifierOverlap(
  getUserIdentifiers(user),
  getProjectStewardIdentifiers(project),
);

export const reviewerMatchesCurrentUser = (
  reviewer: Pick<Reviewer, 'user_email' | 'user_name'>,
  user: AuthLikeUser,
): boolean => {
  const reviewerIdentifiers = new Set<string>();
  addIdentifier(reviewerIdentifiers, reviewer.user_email);
  addIdentifier(reviewerIdentifiers, reviewer.user_name);

  return hasIdentifierOverlap(reviewerIdentifiers, getUserIdentifiers(user));
};

export const isProjectStewardReviewer = (
  reviewer: Pick<Reviewer, 'user_email' | 'user_name' | 'is_owner'>,
  project: ProjectOwnershipContext,
): boolean => {
  if (reviewer.is_owner ?? false) {
    return true;
  }

  const reviewerIdentifiers = new Set<string>();
  addIdentifier(reviewerIdentifiers, reviewer.user_email);
  addIdentifier(reviewerIdentifiers, reviewer.user_name);

  return hasIdentifierOverlap(
    reviewerIdentifiers,
    getProjectStewardIdentifiers(project),
  );
};